import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { soundManager, SOUNDS } from "../../systems/SoundManager";
// Icons removed - text-only UI

// ============================================================================
// NEGOTIATION RESULT COMPONENT - Dramatic Reveal
// ============================================================================

export default function NegotiationResult({
  airportCode,
  airportName,
  playerBids = [],
  competitorBids = [],
  results = [],
  reputationChanges = [],
  relationshipChanges = [],
  totalCost = 0,
  onContinue,
  onViewSchedule,
}) {
  // Animation state
  const [phase, setPhase] = useState("sealed"); // sealed, revealing, summary
  const [currentSlotIndex, setCurrentSlotIndex] = useState(-1);
  const [revealedCompetitors, setRevealedCompetitors] = useState({}); // slotId -> number of revealed competitors
  const [slotWinnerRevealed, setSlotWinnerRevealed] = useState({}); // slotId -> boolean

  // Start reveal sequence after mount
  useEffect(() => {
    // Initial suspense pause
    const timer1 = setTimeout(() => {
      setPhase("revealing");
      setCurrentSlotIndex(0);
    }, 2000);

    return () => clearTimeout(timer1);
  }, []);

  // Reveal slots one by one
  useEffect(() => {
    if (phase !== "revealing" || currentSlotIndex < 0) return;
    if (currentSlotIndex >= results.length) {
      // All slots revealed, show summary
      const timer = setTimeout(() => setPhase("summary"), 1500);
      return () => clearTimeout(timer);
    }

    const slotId = results[currentSlotIndex]?.slotId;
    if (!slotId) return;

    // Get competitors for this slot
    const slotCompetitors = competitorBids.filter(b => b.slotId === slotId);
    const currentRevealed = revealedCompetitors[slotId] || 0;

    if (currentRevealed < slotCompetitors.length) {
      // Reveal next competitor
      const timer = setTimeout(() => {
        setRevealedCompetitors(prev => ({
          ...prev,
          [slotId]: (prev[slotId] || 0) + 1,
        }));
      }, 800);
      return () => clearTimeout(timer);
    } else if (!slotWinnerRevealed[slotId]) {
      // Reveal winner
      const timer = setTimeout(() => {
        setSlotWinnerRevealed(prev => ({ ...prev, [slotId]: true }));
        // Play slot won sound if player won this slot
        const slotResult = results.find(r => r.slotId === slotId);
        if (slotResult?.winner === "player") {
          soundManager.play(SOUNDS.SLOT_WON);
        }
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Move to next slot
      const timer = setTimeout(() => {
        setCurrentSlotIndex(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [phase, currentSlotIndex, revealedCompetitors, slotWinnerRevealed, results, competitorBids]);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toLocaleString()}`;
  };

  // Calculate summary stats
  const slotsWon = results.filter(r => r.winner === "player").length;
  const slotsLost = results.filter(r => r.winner !== "player").length;
  const competitorsBeaten = [...new Set(
    results.filter(r => r.winner === "player")
      .flatMap(r => competitorBids.filter(b => b.slotId === r.slotId).map(b => b.airline))
  )];
  const lostTo = [...new Set(
    results.filter(r => r.winner !== "player").map(r => r.winner)
  )];

  // Render sealed phase
  if (phase === "sealed") {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
        <div className="text-center">
          <div className="mb-8">
            <div className="w-24 h-24 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <span className="text-amber-400 text-4xl">⏱</span>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Bids Sealed</h1>
            <p className="text-gray-400">The airport authority is reviewing all submissions...</p>
          </div>
          <div className="flex justify-center gap-2">
            <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <div className="w-3 h-3 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        </div>
      </div>
    );
  }

  // Render revealing phase
  if (phase === "revealing") {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
        <div className="max-w-3xl w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">
              {airportName || airportCode} - Bid Results
            </h1>
            <p className="text-gray-400">Revealing slot allocations...</p>
          </div>

          {/* Slot Cards */}
          <div className="space-y-4">
            {results.map((result, index) => {
              const isCurrentSlot = index === currentSlotIndex;
              const isPastSlot = index < currentSlotIndex;
              const isFutureSlot = index > currentSlotIndex;

              if (isFutureSlot) {
                return (
                  <Card key={result.slotId} className="bg-gray-800/50 border-gray-700 opacity-30">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-center">
                        <Badge className="bg-gray-600">{result.slotId}</Badge>
                        <span className="ml-3 text-gray-500">Awaiting reveal...</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              }

              return (
                <SlotRevealCard
                  key={result.slotId}
                  result={result}
                  playerBid={playerBids.find(b => b.slotId === result.slotId)}
                  competitors={competitorBids.filter(b => b.slotId === result.slotId)}
                  revealedCount={revealedCompetitors[result.slotId] || 0}
                  winnerRevealed={slotWinnerRevealed[result.slotId] || false}
                  isActive={isCurrentSlot}
                  formatCurrency={formatCurrency}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Render summary phase
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="max-w-4xl w-full my-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 ${
            slotsWon > 0 ? "bg-green-500/20" : "bg-red-500/20"
          }`}>
            {slotsWon > 0 ? (
              <span className="text-green-400 text-4xl font-bold">WIN</span>
            ) : (
              <span className="text-red-400 text-4xl">✗</span>
            )}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {slotsWon > 0 ? "Negotiation Successful!" : "Negotiation Failed"}
          </h1>
          <p className="text-gray-400">
            {slotsWon > 0
              ? `You secured ${slotsWon} slot${slotsWon > 1 ? "s" : ""} at ${airportCode}`
              : `You were outbid on all slots at ${airportCode}`
            }
          </p>
        </div>

        {/* Results Grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Slots Summary */}
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">
                Slot Results
              </h3>
              <div className="space-y-3">
                {results.map((result) => {
                  const playerBid = playerBids.find(b => b.slotId === result.slotId);
                  const won = result.winner === "player";
                  return (
                    <div key={result.slotId} className={`p-3 rounded-lg ${
                      won ? "bg-green-500/10 border border-green-500/30" : "bg-red-500/10 border border-red-500/30"
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={won ? "bg-green-600" : "bg-red-600"}>
                            {result.slotId}
                          </Badge>
                          <span className={won ? "text-green-400" : "text-red-400"}>
                            {won ? "✓" : "✗"}
                          </span>
                        </div>
                        <div className="text-right">
                          <div className={`font-medium ${won ? "text-green-400" : "text-red-400"}`}>
                            {won ? "WON" : "LOST"}
                          </div>
                          <div className="text-xs text-gray-500">
                            Your bid: {formatCurrency(playerBid?.amount || 0)}
                          </div>
                        </div>
                      </div>
                      {!won && (
                        <div className="mt-2 text-sm text-gray-400">
                          <span className="text-red-300">{result.winner}</span> bid {formatCurrency(result.winningBid)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">
                $ Financial Summary
              </h3>
              {slotsWon > 0 ? (
                <div className="space-y-3">
                  <div className="flex justify-between p-2 bg-gray-700/50 rounded">
                    <span className="text-gray-300">Slots Acquired</span>
                    <span className="text-white font-medium">{slotsWon}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-700/50 rounded">
                    <span className="text-gray-300">Total Cost</span>
                    <span className="text-green-400 font-medium">{formatCurrency(totalCost)}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-gray-700/50 rounded">
                    <span className="text-gray-300">Monthly Commitment</span>
                    <span className="text-amber-400 font-medium">{formatCurrency(slotsWon * 30000)}/mo</span>
                  </div>
                  <div className="border-t border-gray-700 pt-3 mt-3">
                    <div className="text-sm text-amber-400">
                      ! First flight due within 14 days
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-400">No financial commitment required</p>
                  <p className="text-sm text-gray-500 mt-2">
                    You may negotiate again when new slots become available
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Reputation & Relationships */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {/* Reputation Changes */}
          {reputationChanges.length > 0 && (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">
                  Reputation Impact
                </h3>
                <div className="space-y-2">
                  {reputationChanges.map((change, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-700/50 rounded">
                      <span className={change.amount > 0 ? "text-green-400" : "text-red-400"}>
                        {change.amount > 0 ? "↑" : "↓"} {change.amount > 0 ? "+" : ""}{change.amount}
                      </span>
                      <span className="text-gray-300">{change.reason}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Relationship Changes */}
          {relationshipChanges.length > 0 && (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">
                  Competitor Relations
                </h3>
                <div className="space-y-2">
                  {relationshipChanges.map((change, i) => (
                    <div key={i} className="p-2 bg-gray-700/50 rounded">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-orange-400 border-orange-400">
                          {change.airline}
                        </Badge>
                        <span className={`text-sm ${
                          change.change === "hostile" ? "text-red-400" :
                          change.change === "competitive" ? "text-orange-400" :
                          change.change === "neutral" ? "text-gray-400" :
                          "text-green-400"
                        }`}>
                          {change.change}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{change.reason}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Competitors Summary */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">
              Competition Summary
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {competitorsBeaten.length > 0 && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="text-green-400 font-medium mb-2">Outbid:</div>
                  <div className="flex flex-wrap gap-2">
                    {competitorsBeaten.map((airline, i) => (
                      <Badge key={i} className="bg-green-600/50">{airline}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {lostTo.length > 0 && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="text-red-400 font-medium mb-2">Lost to:</div>
                  <div className="flex flex-wrap gap-2">
                    {lostTo.map((airline, i) => (
                      <Badge key={i} className="bg-red-600/50">{airline}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-center gap-4">
          {slotsWon > 0 && onViewSchedule && (
            <Button
              onClick={onViewSchedule}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Schedule Flights
            </Button>
          )}
          <Button
            onClick={onContinue}
            variant={slotsWon > 0 ? "outline" : "default"}
            className={slotsWon > 0 ? "" : "bg-gray-600 hover:bg-gray-700"}
          >
            {slotsWon > 0 ? "Continue →" : "Return to Operations →"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SLOT REVEAL CARD SUB-COMPONENT
// ============================================================================

function SlotRevealCard({
  result,
  playerBid,
  competitors,
  revealedCount,
  winnerRevealed,
  isActive,
  formatCurrency,
}) {
  const won = result.winner === "player";

  return (
    <Card className={`bg-gray-800 border-2 transition-all duration-500 ${
      isActive ? "border-amber-500 shadow-lg shadow-amber-500/20" :
      winnerRevealed ? (won ? "border-green-500" : "border-red-500") :
      "border-gray-700"
    }`}>
      <CardContent className="p-4">
        {/* Slot Header */}
        <div className="flex items-center justify-between mb-4">
          <Badge className={`text-lg px-3 py-1 ${
            winnerRevealed ? (won ? "bg-green-600" : "bg-red-600") : "bg-purple-600"
          }`}>
            {result.slotId}
          </Badge>
          {winnerRevealed && (
            <div className={`font-bold ${won ? "text-green-400" : "text-red-400"}`}>
              {won ? "YOU WIN!" : "OUTBID"}
            </div>
          )}
        </div>

        {/* Bids */}
        <div className="space-y-2">
          {/* Player Bid - Always visible */}
          <div className={`flex items-center justify-between p-3 rounded-lg transition-all ${
            winnerRevealed
              ? (won ? "bg-green-500/20 border border-green-500/50" : "bg-gray-700/50")
              : "bg-blue-500/20 border border-blue-500/50"
          }`}>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-600">YOU</Badge>
              <span className="text-white font-medium">Your Airline</span>
            </div>
            <span className={`font-bold text-lg ${
              winnerRevealed && won ? "text-green-400" : "text-white"
            }`}>
              {formatCurrency(playerBid?.amount || 0)}
            </span>
          </div>

          {/* Competitor Bids - Revealed progressively */}
          {competitors.map((comp, index) => {
            const isRevealed = index < revealedCount;
            const isWinner = winnerRevealed && result.winner === comp.airline;

            if (!isRevealed) {
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-gray-500 text-gray-500">?</Badge>
                    <span className="text-gray-500">Revealing...</span>
                  </div>
                  <span className="text-gray-500">???</span>
                </div>
              );
            }

            return (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg transition-all duration-500 ${
                  isWinner
                    ? "bg-red-500/20 border border-red-500/50"
                    : "bg-gray-700/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={isWinner ? "border-red-400 text-red-400" : "border-orange-400 text-orange-400"}>
                    {isWinner && "* "}
                    {(comp.airlineName || comp.airline || "Unknown").split(" ")[0]}
                  </Badge>
                  <span className="text-gray-300">{comp.airlineName || comp.airline || "Unknown Airline"}</span>
                </div>
                <span className={`font-bold ${isWinner ? "text-red-400" : "text-gray-300"}`}>
                  {formatCurrency(comp.amount)}
                </span>
              </div>
            );
          })}
        </div>

        {/* Winner Announcement */}
        {winnerRevealed && !won && (
          <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-center">
            <span className="text-red-300 text-sm">
              {result.winner} secured this slot with a bid of {formatCurrency(result.winningBid)}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// HELPER: Generate sealed competitor bids (BEFORE player bids)
// Per CLAUDE_PROTOCOL.md Section 8.3:
// - AI bids are based on airport tier, not player bid - true sealed auction
// - AI considers their own relations with airport (loyalty)
// - Hostile competitors (negative relations with player) bid more aggressively
// ============================================================================

export function generateSealedCompetitorBids(slots, competitors, airportTier = 1, airportData = {}, relationsState = null) {
  const bids = [];
  const airportCode = airportData.airportCode;

  // Base bid scales with tier (Tier 1 = $50K base, Tier 5 = $250K base)
  const baseBid = 50000 * airportTier;

  // Is this airport a major hub? (increases competition)
  const isHub = airportData.isHub || false;
  const prestigeMultiplier = isHub ? 1.5 : 1.0;

  // Scarcity multiplier - fewer slots = higher bids
  const totalSlots = airportData.totalSlots || 10;
  const availableSlots = slots.length;
  const scarcityMultiplier = 1 + (1 - availableSlots / totalSlots);

  for (const slot of slots) {
    // Each competitor has a chance to bid on each slot
    for (const competitor of competitors) {
      // Handle both old format (string) and new format (object with id/name/aggression)
      const airlineId = typeof competitor === 'string' ? competitor : competitor.id;
      const airlineName = typeof competitor === 'string' ? competitor : competitor.name;
      const baseAggression = typeof competitor === 'string' ? 5 : (competitor.aggression || 5);

      // Get competitor's loyalty to this airport (0-100)
      const airportLoyalty = relationsState?.airports?.[airportCode]?.loyalty?.[airlineId] || 0;
      // Competitors with higher loyalty at this airport bid more (they want to maintain dominance)
      const loyaltyMultiplier = 1 + (airportLoyalty / 200); // 0 loyalty = 1x, 100 loyalty = 1.5x

      // Get player relations with this competitor (negative = hostile)
      const playerRelations = relationsState?.playerRelations?.competitors?.[airlineId]?.current || 0;
      // Hostile competitors bid more aggressively AGAINST the player
      const hostilityMultiplier = playerRelations < -20 ? 1.2 : (playerRelations < 0 ? 1.1 : 1.0);

      // 60-80% chance competitor bids on this slot (varies by aggression)
      const bidChance = 0.5 + (baseAggression / 20); // aggression 5 = 75%, aggression 9 = 95%
      if (Math.random() > bidChance) continue;

      // Aggression multiplier from competitor personality (1-10 scale -> 0.7x to 1.3x)
      const aggressionMultiplier = 0.7 + (baseAggression / 15);

      // Random variance to prevent predictable bids
      const variance = 0.9 + Math.random() * 0.2; // 90% to 110%

      // Calculate final bid with all factors
      const aiBid = baseBid * prestigeMultiplier * aggressionMultiplier * scarcityMultiplier * loyaltyMultiplier * hostilityMultiplier * variance;

      // Round to nearest 5000
      const amount = Math.round(aiBid / 5000) * 5000;

      bids.push({
        slotId: slot.id,
        airline: airlineId, // Use ID for relations lookups
        airlineName: airlineName, // Keep name for display
        amount: Math.max(50000, amount), // Minimum bid $50K
      });
    }
  }

  return bids;
}

// ============================================================================
// HELPER: Generate competitor bids based on player's bid (DEPRECATED)
// Kept for backwards compatibility - use generateSealedCompetitorBids instead
// ============================================================================

export function generateCompetitorBids(playerBids, competitors, difficulty = "normal") {
  // If sealed bids were already generated, this shouldn't be called
  // But keeping for safety during transition
  const bids = [];

  // Difficulty affects how aggressive competitors bid
  const difficultyMod = {
    easy: 0.7,
    normal: 1.0,
    hard: 1.3,
  }[difficulty] || 1.0;

  for (const playerBid of playerBids) {
    // Each competitor has a chance to bid on each slot
    for (const airline of competitors) {
      // 70% chance competitor bids on this slot
      if (Math.random() > 0.7) continue;

      // Competitor bid varies around player's bid
      const variance = 0.7 + Math.random() * 0.6; // 70% to 130% of player bid
      const baseBid = playerBid.amount * variance * difficultyMod;

      // Round to nearest 5000
      const amount = Math.round(baseBid / 5000) * 5000;

      // Minimum bid is 50000
      bids.push({
        slotId: playerBid.slotId,
        airline,
        amount: Math.max(50000, amount),
      });
    }
  }

  return bids;
}

// ============================================================================
// HELPER: Determine winners for each slot
// Per CLAUDE_PROTOCOL.md Section 8.2:
// - If bids are EQUAL, winner determined by RELATIONS with that airport
// - Higher relations = wins the tie
// - If relations also equal = random selection
// ============================================================================

export function determineWinners(playerBids, competitorBids, airportCode = null, relationsState = null) {
  const results = [];

  // Get player's relations with this airport (default 0 if not available)
  const playerRelations = relationsState?.playerRelations?.airports?.[airportCode]?.current || 0;

  for (const playerBid of playerBids) {
    const slotCompetitors = competitorBids.filter(b => b.slotId === playerBid.slotId);

    // Find highest bid amount first
    let highestBid = playerBid.amount;
    for (const comp of slotCompetitors) {
      if (comp.amount > highestBid) {
        highestBid = comp.amount;
      }
    }

    // Collect all bidders at the highest amount (for tie-breaking)
    const topBidders = [];
    if (playerBid.amount === highestBid) {
      topBidders.push({
        type: "player",
        airline: "player",
        relations: playerRelations
      });
    }
    for (const comp of slotCompetitors) {
      if (comp.amount === highestBid) {
        // Get competitor's relations (airport loyalty to them)
        const compRelations = relationsState?.airports?.[airportCode]?.loyalty?.[comp.airline] || 0;
        topBidders.push({
          type: "competitor",
          airline: comp.airline,
          relations: compRelations
        });
      }
    }

    // TIE-BREAKING per Protocol 8.2:
    // 1. If only one bidder at top, they win
    // 2. If multiple, sort by relations (higher wins)
    // 3. If relations also tied, random selection
    let winner;
    if (topBidders.length === 1) {
      winner = topBidders[0].airline;
    } else {
      // Sort by relations (descending)
      topBidders.sort((a, b) => b.relations - a.relations);

      // Check if top relations are tied
      const topRelations = topBidders[0].relations;
      const tiedAtTop = topBidders.filter(b => b.relations === topRelations);

      if (tiedAtTop.length === 1) {
        // Clear winner by relations
        winner = tiedAtTop[0].airline;
      } else {
        // Relations tied - random selection among those tied
        const winnerIndex = Math.floor(Math.random() * tiedAtTop.length);
        winner = tiedAtTop[winnerIndex].airline;
      }
    }

    results.push({
      slotId: playerBid.slotId,
      winner,
      winningBid: highestBid,
      playerBid: playerBid.amount,
      tieBreaker: topBidders.length > 1 ? "relations" : null,
    });
  }

  return results;
}

// ============================================================================
// HELPER: Generate reputation changes based on results
// ============================================================================

export function generateReputationChanges(results, airportCode) {
  const changes = [];
  const slotsWon = results.filter(r => r.winner === "player").length;
  const totalSlots = results.length;

  if (slotsWon === totalSlots && totalSlots > 0) {
    changes.push({
      type: "market_presence",
      amount: 3,
      reason: `Dominant presence at ${airportCode}`,
    });
  } else if (slotsWon > 0) {
    changes.push({
      type: "market_presence",
      amount: slotsWon,
      reason: `${airportCode} market entry`,
    });
  } else {
    changes.push({
      type: "market_presence",
      amount: -1,
      reason: `Perceived as weak bidder at ${airportCode}`,
    });
  }

  // Check for aggressive bidding (won with significantly higher bids)
  const aggressiveWins = results.filter(r =>
    r.winner === "player" && r.winningBid > r.playerBid * 0.9
  ).length;

  if (aggressiveWins >= 2) {
    changes.push({
      type: "market_reputation",
      amount: 1,
      reason: "Aggressive expansion strategy noted",
    });
  }

  return changes;
}

// ============================================================================
// HELPER: Generate relationship changes with competitors
// ============================================================================

export function generateRelationshipChanges(results, competitorBids) {
  const changes = [];
  const affectedAirlines = [...new Set(competitorBids.map(b => b.airline))];

  for (const airline of affectedAirlines) {
    const competedSlots = competitorBids.filter(b => b.airline === airline);
    const lostToPlayer = results.filter(r =>
      r.winner === "player" &&
      competitorBids.some(b => b.slotId === r.slotId && b.airline === airline)
    ).length;

    if (lostToPlayer >= 2) {
      changes.push({
        airline,
        change: "hostile",
        reason: `Views you as aggressive competitor after losing ${lostToPlayer} slots`,
      });
    } else if (lostToPlayer === 1) {
      changes.push({
        airline,
        change: "competitive",
        reason: "Now monitoring your expansion closely",
      });
    } else {
      // Player lost to this airline
      const beatPlayer = results.filter(r => r.winner === airline).length;
      if (beatPlayer > 0) {
        changes.push({
          airline,
          change: "confident",
          reason: `Secured ${beatPlayer} slot${beatPlayer > 1 ? "s" : ""} over your bids`,
        });
      }
    }
  }

  return changes;
}
